import React, { useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaUpRightFromSquare } from "react-icons/fa6";

gsap.registerPlugin(ScrollTrigger);

const CaseStudySlider = ({ caseStudies }) => {
  const caseStudySectionRef = useRef(null);
  const caseStudyHeadingRef = useRef(null);
  const animationTriggered = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !animationTriggered.current) {
            animationTriggered.current = true;
            animateCaseStudySlider();
            if (caseStudySectionRef.current) {
              observer.unobserve(caseStudySectionRef.current);
            }
          }
        });
      },
      {
        threshold: 0.2,
      }
    );

    const currentRef = caseStudySectionRef.current; // Capture the ref value

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []); // Dependency array is still empty as intended

  const animateCaseStudySlider = () => {
    const heading = caseStudyHeadingRef.current;
    const swiperSlides = caseStudySectionRef.current.querySelectorAll('.swiper-slide');

    const tl = gsap.timeline({ defaults: { duration: 0.8, ease: 'power2.out' } });

    tl.fromTo(
      heading,
      { yPercent: -20, opacity: 0 },
      { yPercent: 0, opacity: 1 }
    );

    tl.fromTo(
      swiperSlides,
      { yPercent: 20, opacity: 0 },
      { yPercent: 0, opacity: 1, stagger: 0.15 },
      '-=0.5'
    );
  };

  return (
    <div className="bg-black py-12" ref={caseStudySectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        <div className=" text-center md:text-left mb-10 md:mb-12" ref={caseStudyHeadingRef}>
          <h2 className="text-3xl md:text-4xl lg:text-[48px] text-white leading-tight font-medium mb-6">Case Studies</h2>
        </div>
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={50}
          slidesPerView={1}
          // pagination={{ clickable: true }}
          loop={true}
          // navigation
          autoplay={{
            delay: 3000,
          }}
          breakpoints={{
            640: {
              slidesPerView: 1,
              spaceBetween: 50,
            },
            768: {
              slidesPerView: 1,
              spaceBetween: 50,
            },
            1024: {
              slidesPerView: 1,
              spaceBetween: 50,
            },
          }}
        >
          {caseStudies.map((study, index) => (
            <SwiperSlide key={index}>
              <div className="flex flex-col md:flex-row w-full bg-white rounded-xl shadow-lg overflow-hidden">
                {/* Left Section (Text Content) */}
                <div className="w-full md:w-2/3 bg-blue-600 text-left text-white p-6 md:p-8 flex flex-col justify-center">
                  <h3 className="text-2xl md:text-3xl font-medium">{study.heading}</h3>
                  <p className="mt-4 font-light text-md md:text-lg line-clamp-6 md:line-clamp-none">{study.content}</p>
                  <a
                    href={study.link}
                    className="mt-6 md:mt-8 inline-flex items-center jusitfy-center text-white text-sm md:text-md no-underline hover:text-gray-200"
                  >
                     More here <FaUpRightFromSquare className="ml-2" />
                  </a>
                </div>

                {/* Right Section (Image) */}
                <div className="w-full md:w-1/3 hidden md:flex justify-center items-center bg-white md:p-4">
                  <img
                    src={study.image}
                    alt="Case Study"
                    className="max-w-full h-auto"
                  />
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default CaseStudySlider;